import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);
  const category = categories.find((c) => c.slug === comic?.categorySlug);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
      .slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-14 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">您要查看的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-block bg-primary-500 hover:bg-primary-600 text-white font-medium px-6 py-2.5 rounded-full text-sm cursor-pointer transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const viewCount = comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views.toString();

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "https://www.weibamanhua.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": comic.category,
        "item": `https://www.weibamanhua.com/category/${comic.categorySlug}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": comic.title,
        "item": `https://www.weibamanhua.com/comic/${comic.slug}`
      }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": comic.title,
    "description": comic.description,
    "author": {
      "@type": "Person",
      "name": comic.author
    },
    "image": comic.coverImage,
    "datePublished": comic.chapters[0]?.date || "2026-01-01",
    "dateModified": comic.updateDate,
    "publisher": {
      "@type": "Organization",
      "name": "尾巴漫画网",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.weibamanhua.com/logo.png"
      }
    },
    "url": `https://www.weibamanhua.com/comic/${comic.slug}`
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `${comic.title} - ${comic.category} - 免费韩国漫画在线阅读 - 尾巴漫画网`,
    "description": comic.description,
    "url": `https://www.weibamanhua.com/comic/${comic.slug}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "尾巴漫画网",
      "url": "https://www.weibamanhua.com/"
    }
  };

  return (
    <>
      <SchemaOrg schema={webPageSchema} />
      <SchemaOrg schema={breadcrumbSchema} />
      <SchemaOrg schema={articleSchema} />
      <Navbar />
      <main className="pt-20 pb-14 min-h-screen">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
            <span>/</span>
            <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors cursor-pointer">{comic.category}</Link>
            <span>/</span>
            <span className="text-foreground-800 font-medium">{comic.title}</span>
          </nav>

          <article className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8 mb-12">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 免费韩国漫画在线阅读`}
                    className="w-full h-full object-cover object-top"
                    width={400}
                    height={560}
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
                    comic.status === '更新中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="text-xs bg-accent-100 text-accent-700 font-medium px-2.5 py-0.5 rounded-full cursor-pointer hover:bg-accent-200 transition-colors"
                  >
                    {comic.category}
                  </Link>
                </div>

                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-600 mb-4">作者：{comic.author}</p>

                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-sm"></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800">{comic.rating}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line text-foreground-400 text-sm"></i>
                    </div>
                    <span className="text-sm text-foreground-500">{viewCount} 阅读</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-line text-foreground-400 text-sm"></i>
                    </div>
                    <span className="text-sm text-foreground-500">更新于 {comic.updateDate}</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 mb-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded">
                      #{tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {comic.description}
                </p>

                <button className="bg-primary-500 hover:bg-primary-600 text-white font-medium px-8 py-3 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors duration-200">
                  开始阅读
                </button>
              </div>
            </div>
          </article>

          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              章节列表
              <span className="text-sm font-normal text-foreground-500 ml-2">共 {comic.chapters.length} 话</span>
            </h2>
            <div className="bg-background-50 rounded-xl border border-background-200/70 divide-y divide-background-200/50">
              {comic.chapters.map((chapter) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between p-4 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs text-foreground-500 font-mono whitespace-nowrap">
                      CH.{chapter.number.toString().padStart(3, '0')}
                    </span>
                    <span className="text-sm text-foreground-800 truncate">{chapter.title}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-foreground-400 hidden sm:block">{chapter.date}</span>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-foreground-400"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}